import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import CakeIcon from "@mui/icons-material/Cake";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WorkIcon from "@mui/icons-material/Work";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import useLoader from "../../../hooks/useLoader";
import CkEditor from "../../../components/common/editor/CkEditor";
import { VariableSidebar, EmailChipInput } from "../../../components/mail-template";
import { getMailTemplates, createMailTemplate, updateMailTemplate } from "../../../services/mailTemplate";

/**
 * Default bodies, matched to what EmailAlertController.php actually substitutes on send.
 * Do NOT reintroduce {{employee_name_color}}, {{first_name}}, {{last_name}}, {{company_email}}
 * or {{current_date}} - the backend never resolves those tokens, so they would go out to
 * employees as literal, unresolved text. Name coloring is applied automatically by the
 * backend to {{employee_name}} / {{spouse_name}} / {{couple_name}} using the Name Highlight
 * Color field below, so the body markup should not wrap those tokens in its own color style.
 */
const DEFAULT_TEMPLATES = {
    birthday: {
        subject: "Happy Birthday {{employee_name}}!",
        body: `<div style="text-align: center; font-family: Arial, sans-serif;">
<h2 style="font-size: 40px; margin: 0 0 12px;">{{employee_name}}</h2>
<p style="font-family: Arial, sans-serif; font-size: 15px; font-weight: bold; line-height: 23px;">Happy birthday to you! Be surrounded by kindness and support! May the faith and love of your family inspire you for great things!</p>
</div>`,
    },
    wedding: {
        subject: "Happy Wedding Anniversary {{employee_name}}!",
        body: `<div style="background-color: #1a0f13; padding: 30px; text-align: center; font-family: Arial, sans-serif; color: #f5f5f5;">
<h2 style="font-size: 32px; font-style: italic; color: #c94c61; margin: 0 0 5px;">Happy Marriage Anniversary</h2>
<p style="font-size: 13px; color: #b0a8ab; margin: 0 0 25px;">Wishing</p>
<h3 style="font-size: 24px; font-style: italic; font-weight: normal; margin: 0 0 15px;">{{couple_name}}</h3>
<div style="display: inline-block; background-color: #5c1d29; color: #ffccd5; padding: 8px 20px; border-radius: 20px; font-size: 13px; margin-bottom: 25px;">{{years_completed}} Wonderful Years Together</div>
<p style="font-size: 14px; line-height: 22px; color: #d0c8cb; padding: 0 20px; margin: 0;">Wishing you both a lifetime of love, laughter, and cherished moments together. May your bond grow stronger with each passing year. Happy Anniversary from the entire {{company_name}} family!</p>
</div>`,
    },
    work_anniversary: {
        subject: "Happy {{years_completed}} Year Work Anniversary {{employee_name}}!",
        body: `<div style="text-align: center; font-family: Arial, sans-serif;">
<p style="font-family: Arial, sans-serif; font-size: 15px; line-height: 23px;">For completing <strong>{{years_completed}} year(s)</strong> of dedicated service with <strong>{{company_name}}</strong>. In recognition of your outstanding contributions.</p>
</div>`,
    },
};

const BIRTHDAY_VARIABLES = [
    {
        title: "Employee Name Fields",
        variables: [
            { label: "First Name", value: "{{first_name}}" },
            { label: "Last Name", value: "{{last_name}}" },
            { label: "Full Name", value: "{{employee_name}}" },
        ],
    },
];

const WEDDING_VARIABLES = [
    {
        title: "Wedding Names",
        variables: [
            { label: "First Name", value: "{{first_name}}" },
            { label: "Last Name", value: "{{last_name}}" },
            { label: "Full Name", value: "{{employee_name}}" },
            { label: "Spouse Name", value: "{{wife_name}}" },
            { label: "Years Completed", value: "{{years_completed}}" },
        ],
    },
];

const WORK_ANNIVERSARY_VARIABLES = [
    {
        title: "Employee Details",
        variables: [
            { label: "Full Name", value: "{{employee_name}}" },
            { label: "Years Completed", value: "{{years_completed}}" },
        ],
    },
];

const getDefaultTemplateName = (type) => {
    switch (type) {
        case "birthday":
            return "Birthday Wishes";
        case "wedding":
            return "Wedding Anniversary";
        case "work_anniversary":
            return "Work Anniversary";
        default:
            return "Mail Template";
    }
};

const DEFAULT_TAGLINE = "Empowering Businesses with Emerging Technologies";
const DEFAULT_COLOR = "#b97c39";

const TABS = [
    { type: "birthday", label: "Birthday Wishes", icon: CakeIcon },
    { type: "wedding", label: "Wedding Anniversary", icon: FavoriteIcon },
    { type: "work_anniversary", label: "Work Anniversary", icon: WorkIcon },
];

const formatEmails = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return String(val).split(",").map((e) => e.trim()).filter(Boolean);
};

const parseRecord = (record, type) => {
    const def = DEFAULT_TEMPLATES[type];
    if (!record) {
        return {
            id: null,
            template_name: getDefaultTemplateName(type),
            type,
            subject: def.subject,
            cc: [],
            bcc: [],
            company_tagline: DEFAULT_TAGLINE,
            employee_name_color: DEFAULT_COLOR,
            email_content: def.body,
            status: 1,
        };
    }
    return {
        id: record.id || record.mail_template_id || null,
        template_name: getDefaultTemplateName(type),
        type,
        subject: record.subject || def.subject,
        cc: formatEmails(record.cc),
        bcc: formatEmails(record.bcc),
        company_tagline: record.companyTagline || record.company_tagline || DEFAULT_TAGLINE,
        employee_name_color: record.colorCode || record.name_color_code || DEFAULT_COLOR,
        email_content: record.emailContent || record.email_content || record.description || def.body,
        status: record.status !== undefined ? Number(record.status) : 1,
    };
};

const getEmptyTemplatesMap = () => ({
    birthday: parseRecord(null, "birthday"),
    wedding: parseRecord(null, "wedding"),
    work_anniversary: parseRecord(null, "work_anniversary"),
});

const MailTemplatePage = () => {
    const { showLoader, hideLoader } = useLoader();
    const editorRef = useRef(null);

    const [templatesMap, setTemplatesMap] = useState(getEmptyTemplatesMap);
    const [activeTemplateType, setActiveTemplateType] = useState("birthday");

    const {
        register,
        handleSubmit,
        control,
        watch,
        reset,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        defaultValues: {
            template_name: templatesMap.birthday.template_name,
            subject: templatesMap.birthday.subject,
            cc: templatesMap.birthday.cc,
            bcc: templatesMap.birthday.bcc,
            company_tagline: templatesMap.birthday.company_tagline,
            employee_name_color: templatesMap.birthday.employee_name_color,
            body: templatesMap.birthday.email_content,
            status: true,
        },
    });

    const loadFormValues = (tmpl, type) => {
        reset({
            template_name: tmpl.template_name || getDefaultTemplateName(type),
            subject: tmpl.subject || "",
            cc: tmpl.cc || [],
            bcc: tmpl.bcc || [],
            company_tagline: tmpl.company_tagline || DEFAULT_TAGLINE,
            employee_name_color: tmpl.employee_name_color || DEFAULT_COLOR,
            body: tmpl.email_content || "",
            status: Number(tmpl.status) === 1,
        });
    };

    const fetchTemplates = async (typeToLoad) => {
        try {
            showLoader();
            const response = await getMailTemplates({ module: "hr" });
            const list =
                response?.data?.data?.mailTemplates ||
                response?.data?.mailTemplates ||
                response?.data?.data ||
                response?.data ||
                [];

            const findByType = (type) => list.find((item) => (item.type || "").toLowerCase() === type) || null;

            const newMap = {
                birthday: parseRecord(findByType("birthday"), "birthday"),
                wedding: parseRecord(findByType("wedding"), "wedding"),
                work_anniversary: parseRecord(findByType("work_anniversary"), "work_anniversary"),
            };

            setTemplatesMap(newMap);
            loadFormValues(newMap[typeToLoad || activeTemplateType], typeToLoad || activeTemplateType);
        } catch (error) {
            toast.error("Failed to load templates. Showing defaults instead.");
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchTemplates("birthday");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabSwitch = (type) => {
        if (type === activeTemplateType) return;
        if (isDirty && !window.confirm("You have unsaved changes on this tab. Switch and discard them?")) {
            return;
        }
        setActiveTemplateType(type);
        loadFormValues(templatesMap[type], type);
    };

    const handleResetToDefault = () => {
        const def = DEFAULT_TEMPLATES[activeTemplateType];
        setValue("subject", def.subject, { shouldDirty: true });
        setValue("body", def.body, { shouldDirty: true, shouldValidate: true });
        setValue("employee_name_color", DEFAULT_COLOR, { shouldDirty: true });
        setValue("company_tagline", DEFAULT_TAGLINE, { shouldDirty: true, shouldValidate: true });
        toast.info("Reset to original default template successfully.");
    };

    // Inserts at the last known cursor position inside the CKEditor instance - and only
    // there. Deliberately does NOT require the editor to currently have DOM focus: clicking
    // a variable button blurs the editor before this handler runs (mousedown fires first),
    // so gating on "is focused right now" made the insert fail almost every time. CKEditor
    // keeps its model selection (the cursor position) even after losing focus, so
    // `selection.getFirstPosition()` still points at the right spot.
    const insertVariable = (variable) => {
        const editorInstance = editorRef.current;

        if (!editorInstance) {
            toast.error("Editor is not ready yet - please wait a moment and try again.");
            return;
        }

        const selection = editorInstance.model.document.selection;
        const insertPosition = selection.getFirstPosition() || editorInstance.model.document.getRoot().getPositionOfLeaf?.(0) || editorInstance.model.createPositionAt(editorInstance.model.document.getRoot(), "end");

        editorInstance.model.change((writer) => {
            editorInstance.model.insertContent(writer.createText(variable), insertPosition);
        });

        // Bring focus back to the editor so the caret is visibly active right after the insert.
        editorInstance.editing.view.focus();
    };

    const onSubmit = async (formData) => {
        try {
            showLoader();

            const currentRecord = templatesMap[activeTemplateType];
            const payload = {
                mail_template_id: currentRecord?.id || null,
                template_name: getDefaultTemplateName(activeTemplateType),
                type: activeTemplateType,
                module: "hr",
                subject: formData.subject,
                cc: Array.isArray(formData.cc) ? formData.cc.join(",") : formData.cc,
                bcc: Array.isArray(formData.bcc) ? formData.bcc.join(",") : formData.bcc,
                company_tagline: formData.company_tagline,
                name_color_code: formData.employee_name_color,
                description: "",
                email_content: formData.body,
                status: formData.status ? 1 : 0,
            };

            if (currentRecord?.id) {
                await updateMailTemplate(currentRecord.id, payload);
                toast.success("Template updated successfully.");
            } else {
                await createMailTemplate(payload);
                toast.success("Template created successfully.");
            }

            await fetchTemplates(activeTemplateType);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Something went wrong during save.");
        } finally {
            hideLoader();
        }
    };

    const getCurrentSidebarVariables = () => {
        if (activeTemplateType === "wedding") return WEDDING_VARIABLES;
        if (activeTemplateType === "work_anniversary") return WORK_ANNIVERSARY_VARIABLES;
        return BIRTHDAY_VARIABLES;
    };

    const currentSidebarVariables = getCurrentSidebarVariables();

    return (
        <div className="container-fluid px-3 py-3" style={{ backgroundColor: "var(--bs-tertiary-bg, #f8f9fa)", minHeight: "100vh" }}>
            <div className="row g-3">
                <div className="col-12">
                    <div className="d-flex flex-wrap gap-2" role="tablist">
                        {TABS.map(({ type, label, icon: Icon }) => {
                            const active = activeTemplateType === type;
                            return (
                                <button
                                    key={type}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    disabled={isSubmitting}
                                    onClick={() => handleTabSwitch(type)}
                                    className={`btn btn-sm d-flex align-items-center gap-2 ${active ? "btn-success" : "btn-outline-success"}`}
                                    style={{ fontSize: "12px", fontWeight: 600 }}
                                >
                                    <Icon sx={{ fontSize: 16 }} />
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="col-12">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="card shadow-sm border rounded-2 bg-white">
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "13px" }}>
                                        Template Configuration ({activeTemplateType.toUpperCase()})
                                    </h6>
                                    <button
                                        type="button"
                                        onClick={handleResetToDefault}
                                        disabled={isSubmitting}
                                        className="btn btn-danger btn-sm py-1 px-2 d-flex align-items-center gap-1"
                                        style={{ fontSize: "11px" }}
                                        title="Reset to default template format"
                                    >
                                        <RefreshIcon sx={{ fontSize: 14 }} /> Reset Default
                                    </button>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                                            Template Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            className="form-control form-control-sm py-1 px-2 bg-light text-muted"
                                            style={{ fontSize: "12px" }}
                                            readOnly
                                            {...register("template_name")}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                                            Subject <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            className="form-control form-control-sm py-1 px-2"
                                            style={{ fontSize: "12px" }}
                                            placeholder="Email Subject"
                                            {...register("subject", { required: "Subject is required" })}
                                        />
                                        {errors.subject && (
                                            <small className="text-danger d-block" style={{ fontSize: "10px" }}>
                                                {errors.subject.message}
                                            </small>
                                        )}
                                        {activeTemplateType === "work_anniversary" && (
                                            <small className="text-muted d-block" style={{ fontSize: "10px" }}>
                                                {"{{employee_name}}"} works here, not in the Body - the certificate shows the name automatically.
                                            </small>
                                        )}
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                                            Company Tagline <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            className="form-control form-control-sm py-1 px-2"
                                            style={{ fontSize: "12px" }}
                                            placeholder="Company Tagline"
                                            {...register("company_tagline", { required: "Tagline is required" })}
                                        />
                                        {errors.company_tagline && (
                                            <small className="text-danger d-block" style={{ fontSize: "10px" }}>
                                                {errors.company_tagline.message}
                                            </small>
                                        )}
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                                            Name Highlight Color
                                        </label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color p-0 border-0"
                                                style={{ width: "34px", height: "30px", cursor: "pointer", background: "transparent" }}
                                                value={watch("employee_name_color")}
                                                onChange={(e) => setValue("employee_name_color", e.target.value, { shouldValidate: true, shouldDirty: true })}
                                            />
                                            <input
                                                type="text"
                                                className="form-control form-control-sm py-1 px-2"
                                                style={{ fontSize: "12px" }}
                                                value={watch("employee_name_color")}
                                                onChange={(e) => setValue("employee_name_color", e.target.value, { shouldValidate: true, shouldDirty: true })}
                                                placeholder="#b97c39"
                                            />
                                        </div>
                                        <small className="text-muted d-block" style={{ fontSize: "9.5px" }}>
                                            {activeTemplateType === "work_anniversary"
                                                ? "Applied to the name on the certificate header."
                                                : "Applied automatically wherever the name variable appears."}
                                        </small>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                                            CC Emails
                                        </label>
                                        <Controller
                                            control={control}
                                            name="cc"
                                            render={({ field }) => <EmailChipInput value={field.value} onChange={field.onChange} placeholder="Add CC email..." />}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                                            BCC Emails
                                        </label>
                                        <Controller
                                            control={control}
                                            name="bcc"
                                            render={({ field }) => <EmailChipInput value={field.value} onChange={field.onChange} placeholder="Add BCC email..." />}
                                        />
                                    </div>

                                    <div className="col-12 mt-3">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px", fontWeight: "600" }}>
                                            Email Body & Variables <span className="text-danger">*</span>
                                        </label>

                                        <div className="mb-2 p-2 border rounded bg-light" style={{ borderColor: "var(--bs-border-color, #dee2e6)" }}>
                                            <VariableSidebar variables={currentSidebarVariables} onInsert={insertVariable} />
                                            <small className="text-muted d-block mt-1" style={{ fontSize: "9.5px" }}>
                                                Click inside the editor below first, then click a variable to insert it at the cursor.
                                            </small>
                                        </div>

                                        <Controller
                                            control={control}
                                            name="body"
                                            rules={{ required: "Email body content is required" }}
                                            render={({ field }) => <CkEditor value={field.value} onChange={field.onChange} editorRef={editorRef} />}
                                        />
                                        {errors.body && (
                                            <small className="text-danger d-block" style={{ fontSize: "10px" }}>
                                                {errors.body.message}
                                            </small>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="btn btn-success btn-sm d-flex align-items-center gap-1 px-4 py-2"
                                style={{ fontSize: "12px" }}
                            >
                                <SaveIcon sx={{ fontSize: 16 }} />
                                {isSubmitting ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MailTemplatePage;